import { motion } from "motion/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { AttributionConsent } from "./components/marketing/AttributionConsent";
import { Footer } from "./components/marketing/Footer";
import { Navbar } from "./components/marketing/Navbar";
import { capturePageview } from "./lib/analytics";
import { localeFromPath } from "./lib/i18n";
import { resolvePageMeta } from "./lib/pageMeta";
import { useRevealReducedMotion } from "./lib/reveal";
import { About } from "./routes/About";
import { Contact } from "./routes/Contact";
import { Download } from "./routes/Download";
import { DocsArticle } from "./routes/docs/DocsArticle";
import { DocsHub } from "./routes/docs/DocsHub";
import { FeaturesRedirect } from "./routes/FeaturesRedirect";
import { Landing } from "./routes/Landing";
import { Privacy } from "./routes/legal/Privacy";
import { Terms } from "./routes/legal/Terms";
import { NotFound } from "./routes/NotFound";
import { Pricing } from "./routes/Pricing";
import { Product } from "./routes/Product";
import { Referral } from "./routes/Referral";
import { Security } from "./routes/Security";

function LocaleLayout() {
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const reduceMotion = useRevealReducedMotion();
  const locale = localeFromPath(location.pathname);
  const isHome = location.pathname === "/";
  const isDocs = location.pathname === "/docs" || location.pathname.startsWith("/docs/");

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    document.documentElement.lang = locale;
    const meta = resolvePageMeta(location.pathname, t);
    document.title = meta.title;
    const description = document.querySelector('meta[name="description"]');
    if (description instanceof HTMLMetaElement) {
      description.content = meta.description;
    }
    // Keep the canonical URL on the current route — the static tag in
    // index.html only covers the homepage.
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical instanceof HTMLLinkElement) {
      canonical.href = `https://mavori.ai${location.pathname === "/" ? "/" : location.pathname}`;
    }
    // SPA `$pageview` — PostHog auto-enriches with geo (country/city) and
    // referrer/UTM at ingest, so this alone answers "where from + which page".
    capturePageview(location.pathname);
  }, [locale, i18n, t, location.pathname]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: route state and motion preference deliberately drive the handoff
  useEffect(() => {
    // `/#waitlist` and friends should land on the section, not the top. The
    // element is present by the time this effect runs after the route renders.
    if (location.hash) {
      const target = document.querySelector(location.hash);
      if (target) {
        target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    document.getElementById("content")?.focus({ preventScroll: true });
  }, [location.pathname, location.hash, reduceMotion]);

  if (isHome) {
    return (
      <>
        <Outlet />
        <AttributionConsent />
      </>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <a
        href="#content"
        className="sr-only fixed top-3 left-3 z-[60] rounded-md bg-card px-3 py-2 text-sm font-medium text-foreground shadow-editorial focus:fixed focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-primary"
      >
        Skip to content
      </a>
      {/* Same glass pill as the home hero, pinned above interior pages. */}
      <div className="sticky top-0 z-50">
        <Navbar />
      </div>
      {/* Reading routes change immediately; marketing pages retain the softer
          reveal used by the rest of the site. */}
      <motion.main
        id="content"
        tabIndex={-1}
        key={location.pathname}
        initial={reduceMotion || isDocs ? false : { opacity: 0, y: 10, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={isDocs ? { duration: 0 } : { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="flex-1"
      >
        <Outlet />
      </motion.main>
      <Footer />
      <AttributionConsent />
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route element={<LocaleLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/product" element={<Product />} />
        <Route path="/features" element={<FeaturesRedirect />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/docs" element={<DocsHub />} />
        <Route path="/docs/:slug" element={<DocsArticle />} />
        <Route path="/security" element={<Security />} />
        <Route path="/about" element={<About />} />
        <Route path="/download" element={<Download />} />
        {/* Contact lists every channel with its literal URL / email — the
            reliable, crawlable fallback when a mailto:/logo link goes nowhere. */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal/terms" element={<Terms />} />
        <Route path="/legal/privacy" element={<Privacy />} />
        <Route path="/referral" element={<Referral />} />
        {/* The standalone disclaimer page was removed; its substance lives in Terms §6–8.
            The redirect stays for old external links and crawler indexes. */}
        <Route path="/legal/disclaimer" element={<Navigate to="/" replace />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
